/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectGeneralSettings } from "store/slices/generalSettingsSlice";
import { Paper, Stack, TextField, Autocomplete } from "@mui/material";
import Button from "components/generals/Button";
import DefaultCell from "components/generals/DefaultCell";
import DataTable from "dashboard-template/examples/Tables/DataTable";
import CurrencyInput from "components/generals/CurrencyInput";
import PercentageInput from "components/generals/PercentageInput";
import FormControlLabel from "components/generals/FormControlLabel";
import Switch from "components/generals/Switch";
import Grid from "components/generals/Grid";
import Typography from "components/generals/Typography";

/**
 * Pricing item selected for billing encounter.
 * 
 * @interface SelectedPricingItem
 * @property {string} id - Pricing item ID
 * @property {string} name - Service/procedure name
 * @property {string} description - Detailed description
 * @property {number} price - Base price before discounts
 * @property {string} [discountType] - Type of discount ("Percentage", "Fixed", "No Discount")
 * @property {number} [discountValue] - Discount amount or percentage
 * @property {string} uniqueId - Unique identifier for this item in the list
 * @property {string} cpt - CPT code for billing
 * @property {boolean} isClaim - Whether this is an insurance claim
 * @property {boolean} [coInsuranceApplicable] - Whether co-insurance applies
 * @property {string} [billingEncounterId] - ID of the associated billing encounter
 */
interface SelectedPricingItem {
  id: string;
  name: string;
  description: string;
  price: number;
  discountType?: string;
  discountValue?: number;
  uniqueId: string;
  cpt: string;
  isClaim: boolean;
  coInsuranceApplicable?: boolean;
  billingEncounterId?: string;
}

/**
 * Properties for the BillingEncounterTable component.
 * 
 * @interface BillingEncounterTableProps
 * @property {SelectedPricingItem[]} selectedPricing - Array of selected pricing items
 * @property {Function} setSelectedPricing - Callback to update selected pricing list
 * @property {Function} onRemoveItem - Callback to remove item by uniqueId
 * @property {boolean} insurance - Whether visit has insurance coverage
 * @property {boolean} nonBillable - Whether visit is non-billable
 */
interface BillingEncounterTableProps {
  selectedPricing: SelectedPricingItem[];
  setSelectedPricing: React.Dispatch<React.SetStateAction<SelectedPricingItem[]>>;
  onRemoveItem: (uniqueId: string) => void;
  insurance: boolean;
  nonBillable: boolean;
}

/**
 * Billing encounter table component for managing appointment billing items.
 * Allows inline editing of discounts, claims status, and co-insurance settings.
 * 
 * Features:
 * - Interactive pricing table with discount management
 * - Support for percentage and fixed amount discounts
 * - Toggle insurance claim status per item
 * - Co-insurance applicability toggle
 * - Automatic final price calculation with discounts
 * - Conditional logic for insurance/non-billable visits
 * - Safe array handling with default empty arrays
 * - Inline editing via Material-UI inputs
 * 
 * Discount Types:
 * - "No Discount": No discount applied (default)
 * - "Percentage": Discount as percentage of price
 * - "Fixed": Fixed dollar amount discount
 * 
 * Switch Logic:
 * - insurance=true, nonBillable=true: enabled, default false
 * - insurance=true only: disabled, default true (force claim)
 * - nonBillable=true only: disabled, default false (no claim)
 * - Both false: enabled, default false
 * 
 * @param {BillingEncounterTableProps} props - Component properties
 * @returns {JSX.Element} Billing encounter table with inline editing
 */
const BillingEncounterTable: React.FC<BillingEncounterTableProps> = ({
  selectedPricing,
  setSelectedPricing,
  onRemoveItem,
  insurance,
  nonBillable
}) => {
  const { t } = useTranslation();
  const generalSettings = useSelector(selectGeneralSettings);
  const isInsuranceModuleEnabled = generalSettings?.isInsuranceModuleEnabled ?? true;

  const discountTypeTranslations = {
    "No Discount": t("billingEncounterForm.noDiscount"),
    "Percentage": t("billingEncounterForm.percentage"),
    "Amount": t("billingEncounterForm.amount"),
  };

  /**
   * Handles discount type change for a pricing item.
   * Resets discount value to 0 when type changes.
   * 
   * @param {number} index - Index of item in selectedPricing array
   * @param {string | null} value - New discount type ("Percentage", "Fixed", or null)
   */
  const handleDiscountTypeChange = (index: number, value: string | null) => {
    const updatedPricing = [...selectedPricing];
    updatedPricing[index].discountType = value || "No Discount";
    updatedPricing[index].discountValue = 0;
    setSelectedPricing(updatedPricing);
  };

  /**
   * Handles discount value change for a pricing item.
   * 
   * @param {number} index - Index of item in selectedPricing array
   * @param {number} value - New discount value (percentage or fixed amount)
   */
  const handleDiscountValueChange = (index: number, value: number) => {
    const updatedPricing = [...selectedPricing];
    updatedPricing[index].discountValue = value;
    setSelectedPricing(updatedPricing);
  };

  /**
   * Calculates final price after applying discount.
   * 
   * Discount Logic:
   * - No discount: returns original price
   * - Percentage: subtracts (discountValue/100) * price
   * - Fixed: subtracts discountValue
   * 
   * @param {SelectedPricingItem} item - Pricing item with discount info
   * @returns {number} Final price after discount
   */
  const calculateFinalPrice = (item: SelectedPricingItem): number => {
    if (!item.discountType || item.discountType === "No Discount") {
      return Number(item.price);
    }

    const discount =
      item.discountType === "Percentage"
        ? (Number(item.discountValue) / 100) * Number(item.price)
        : Number(item.discountValue) || 0;

    return Number(item.price) - discount;
  };

  const getSwitchConfig = useMemo(() => {

    if (insurance && nonBillable) {
      return { disabled: false, defaultValue: false };
    } else if (insurance) {
      return { disabled: true, defaultValue: true };
    } else if (nonBillable) {
      return { disabled: true, defaultValue: false };
    }
    return { disabled: false, defaultValue: false };
  }, [insurance, nonBillable]);

  const getUITable = (list: SelectedPricingItem[]) => {
    const safeList = Array.isArray(list) ? list : [];

    // Build columns array conditionally based on insurance module
    const columns: any[] = [];

    // Add payer column only if insurance module is enabled
    if (isInsuranceModuleEnabled) {
      columns.push({
        Header: t("billingEncounterForm.payer"),
        id: "payer",
        Cell: ({ row }: { row: any }) => {
          const service: SelectedPricingItem = row.original;
          if (!insurance) {
            service.isClaim = insurance;
          }
          const { disabled, defaultValue } = getSwitchConfig;
          return (
            <FormControlLabel
              id={"cy-isClaim-switch" + row.index}
              checked={service.isClaim}
              className="billing-encounter__text-table"
              control={
                <Switch
                  label={service.isClaim ? t("billingEncounterForm.insurance") : t("billingEncounterForm.patient")}
                  name={`isClaim-${service.uniqueId}`}
                  value={service.isClaim}
                  setFieldValue={(_name, value) => {
                    const updated = [...selectedPricing];
                    const index = updated.findIndex(i => i.uniqueId === service.uniqueId);
                    if (index >= 0) {
                      updated[index].isClaim = !!value;
                      if (!value) {
                        updated[index].coInsuranceApplicable = false;
                      }
                      setSelectedPricing(updated);
                    }
                  }}
                  color="primary"
                  helper={false}
                  disabled={disabled}
                  tooltip={disabled ? (defaultValue ? t("billingEncounterForm.enableExplainInsurnace") : t("billingEncounterForm.enableExplainPatients")) : undefined}
                />
              }
              label=""
            />
          );
        },
      });
    }

    // Add common columns
    columns.push(
      {
        Header: t("billingEncounterForm.cpt"),
        accessor: "cpt",
        sortIndicatorType: "az",
        Cell: ({ value }: { value: any }) => {
          return <DefaultCell value={value || "N/A"} />;
        },
      },
      {
        Header: t("billingEncounterForm.service"),
        accessor: "name",
        sortIndicatorType: "az",
        Cell: ({ value }: { value: any }) => {
          return <DefaultCell value={value || "N/A"} />;
        },
      },
      {
        Header: t("billingEncounterForm.price"),
        accessor: "price",
        sortIndicatorType: "numeric",
        Cell: ({ value }: { value: any }) => {
          return <DefaultCell value={`$${Number(value).toFixed(2)}`} />;
        },
      },
      {
        Header: t("billingEncounterForm.discount"),
        id: "discount",
        sortIndicatorType: "numeric",
        Cell: ({ row }: { row: any }) => {
          const service: SelectedPricingItem = row.original;
          const index = selectedPricing.findIndex((item) => item.uniqueId === service.uniqueId);

          return (
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Autocomplete
                options={["No Discount", "Percentage", "Amount"]}
                value={service.discountType || "No Discount"}
                onChange={(_, value) => handleDiscountTypeChange(index, value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("billingEncounterForm.discount")}
                    size="small"
                    sx={{ width: 151 }}
                  />
                )}
                getOptionLabel={(option) => discountTypeTranslations[option] || option}
                size="small"
              />
              {service.discountType &&
                service.discountType !== "No Discount" &&
                (service.discountType === "Percentage" ? (
                  <PercentageInput
                    key={`discount-${service.uniqueId}-${service.discountValue}`}
                    value={service.discountValue || 0}
                    onChange={(value) => handleDiscountValueChange(index, value)}
                    maxValue={100}
                    minValue={0}
                    decimals={2}
                    size="small"
                    sx={{ width: 80 }}
                  />
                ) : (
                  <CurrencyInput
                    key={`discount-${service.uniqueId}-${service.discountValue}`}
                    value={service.discountValue || 0}
                    onChange={(value) => handleDiscountValueChange(index, value)}
                    maxValue={Number(service.price) || 999999}
                    minValue={0}
                    showCurrencySymbol={true}
                    size="small"
                    sx={{ width: 100 }}
                  />
                ))}
            </Stack>
          );
        },
      },
      {
        Header: t("billingEncounterForm.finalPrice"),
        id: "finalPrice",
        sortIndicatorType: "numeric",
        Cell: ({ row }: { row: any }) => {
          const service: SelectedPricingItem = row.original;
          const finalPrice = calculateFinalPrice(service);
          return <DefaultCell value={`$${finalPrice.toFixed(2)}`} />;
        },
      }
    );

    // Add coInsuranceApplicable column only if insurance module is enabled
    if (isInsuranceModuleEnabled) {
      columns.push({
        Header: t("billingEncounterForm.coInsuranceApplicable"),
        id: "coInsuranceApplicable",
        Cell: ({ row }: { row: any }) => {
          const service: SelectedPricingItem = row.original;

          return (
            <FormControlLabel
              id="cy-coInsuranceApplicable-switch"
              sx={{ margin: "0 auto", display: "flex", paddingLeft: 0.3 }}
              checked={service.coInsuranceApplicable || false}
              control={
                <Switch
                  label=""
                  name={`coInsuranceApplicable-${service.uniqueId}`}
                  value={service.coInsuranceApplicable || false}
                  setFieldValue={(_name, value) => {
                    const updated = [...selectedPricing];
                    const index = updated.findIndex(i => i.uniqueId === service.uniqueId);
                    if (index >= 0) {
                      updated[index].coInsuranceApplicable = !!value;
                      setSelectedPricing(updated);
                    }
                  }}
                  color="primary"
                  helper={true}
                  disabled={!service.isClaim}
                  tooltip={!service.isClaim ? t("billingEncounterForm.notApplicable") : undefined}
                />
              }
              label=""
            />
          );
        },
      });
    }

    // Add actions column
    columns.push({
      Header: t("billingEncounterForm.actions"),
      id: "actions",
      disableSortBy: true,
      Cell: ({ row }: any) => {
        const service: SelectedPricingItem = row.original;
        return (
          <Button
            id="cy-remove-service-button"
            variant="outlined"
            color="error"
            size="small"
            onClick={() => onRemoveItem(service.uniqueId)}
          >
            {t("billingEncounterForm.remove")}
          </Button>
        );
      },
    });

    return {
      columns,
      rows: safeList,
    };
  };

  if (selectedPricing.length === 0) {
    return (
      <Grid container justifyContent="center" alignItems="center" fontSize={20}>
        <Grid item>
          <Typography>{t("billingEncounterForm.noServicesSelected")}</Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <DataTable
      table={getUITable(selectedPricing)}
      showTotalEntries={false}
      isSorted={false}
      noEndBorder
      canSearch
      canSearchPlaceholder={t("billingEncounterForm.searchPlaceholder")}
      entriesPerPage={false}
      apiPagination={false}
    />
  );
};

export default BillingEncounterTable;
